import { useTranslate } from '@ur-solutions/react-hooks'
import { Building2, Check, LogOut, Settings, User } from 'lucide-react'
import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { UserNode } from '@/modules/users'
import UserThumbOrInitials from '@/modules/users/UserThumbOrInitials'
import { useCompany, useUser } from '@/utils/hooks'
import { signOut } from '@/utils/signOut'

const COMPANY_SEARCH_THRESHOLD = 4

const MenuRow: React.FC<{
  icon: React.ReactNode
  label: string
  onClick: () => void
}> = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-base font-semibold text-neutral-700 transition-colors active:bg-stone-100"
  >
    <span className="text-neutral-400">{icon}</span>
    {label}
  </button>
)

interface MobileUserMenuProps {
  open: boolean
  user: UserNode
  onClose: () => void
}

export const MobileUserMenu: React.FC<MobileUserMenuProps> = ({
  open,
  user,
  onClose,
}) => {
  const translations = useTranslate({
    myProfile: 'users.my-profile',
    settings: 'users.settings',
    changeCompany: 'common.change-company',
    currentCompany: 'common.current-company',
    searchCompany: 'common.search-company',
    noCompaniesFound: 'common.no-companies-found',
    logout: 'common.log-out',
  })

  const navigate = useNavigate()

  const me = useUser()
  const company = useCompany()

  const [companySearch, setCompanySearch] = useState('')

  const companies = me.companies.filter(other => other.id !== company.id)
  const showCompanySearch = companies.length > COMPANY_SEARCH_THRESHOLD

  const filteredCompanies = companies.filter(other =>
    other.fullName
      .toLocaleLowerCase()
      .includes(companySearch.toLocaleLowerCase())
  )

  function handleNavigate(to: string) {
    return () => {
      onClose()
      void navigate(to)
    }
  }

  return (
    <Drawer
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose()
      }}
    >
      <DrawerContent className="max-h-[88vh]">
        <DrawerHeader className="flex flex-row items-center gap-3 text-left">
          <UserThumbOrInitials
            user={user}
            size={48}
            color="primary"
            backgroundColor="secondary"
            fontSize="0.8em"
            fontWeight="600"
          />

          <div className="min-w-0">
            <DrawerTitle className="truncate text-base">
              {user.fullName}
            </DrawerTitle>
            <DrawerDescription className="truncate">
              {company.fullName}
            </DrawerDescription>
          </div>
        </DrawerHeader>

        <div className="flex min-h-0 flex-col overflow-hidden px-2 pb-4">
          <MenuRow
            icon={<User size={18} />}
            label={translations.myProfile}
            onClick={handleNavigate(`/users/${me.id}`)}
          />
          <MenuRow
            icon={<Settings size={18} />}
            label={translations.settings}
            onClick={handleNavigate('/usersettings')}
          />
          <MenuRow
            icon={<LogOut size={18} />}
            label={translations.logout}
            onClick={() => signOut(onClose)}
          />

          {companies.length > 0 && (
            <div className="mt-2 flex min-h-0 flex-col border-t border-stone-200 pt-3">
              <div className="flex items-center gap-2 px-3 pb-2 text-xs font-semibold tracking-wide text-neutral-400 uppercase">
                <Building2 size={14} />
                {translations.changeCompany}
              </div>

              {showCompanySearch && (
                <div className="px-3 pb-2">
                  <Input
                    type="search"
                    value={companySearch}
                    placeholder={translations.searchCompany}
                    onChange={setCompanySearch}
                  />
                </div>
              )}

              <div className="min-h-0 overflow-y-auto overscroll-contain">
                <div className="flex items-center gap-2 rounded-xl px-3 py-3 text-base font-semibold text-neutral-700">
                  <Check size={16} className="shrink-0 text-secondary" />
                  <span className="truncate">{company.fullName}</span>
                  <span className="ml-auto shrink-0 text-xs font-normal text-neutral-400">
                    {translations.currentCompany}
                  </span>
                </div>

                {filteredCompanies.map(other => (
                  <button
                    key={other.id}
                    type="button"
                    onClick={() => {
                      window.location.href = `/c/${other.shortName}/dashboard`
                    }}
                    className={cn(
                      'flex w-full items-center rounded-xl px-3 py-3 pl-9 text-left text-base',
                      'text-neutral-700 transition-colors active:bg-stone-100'
                    )}
                  >
                    <span className="truncate">{other.fullName}</span>
                  </button>
                ))}

                {filteredCompanies.length === 0 && (
                  <p className="px-3 py-3 pl-9 text-base text-neutral-400">
                    {translations.noCompaniesFound}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
